import React, { useState } from "react";
import { Images, ChevronLeft, ChevronRight, Grid2X2, Trash2, Music } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { t, useLanguage } from "@/lib/i18n";

function AlbumPhoto({ photo, label, fullSize = false }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (failed || !photo.path) return <span role="status" className="flex h-full min-h-24 items-center justify-center p-3 text-sm text-stone-500">{t("Photo unavailable")}</span>;
  if (photo.kind === "audio" || photo.content_type?.startsWith("audio/")) return fullSize
    ? <div className="h-full flex flex-col items-center justify-center gap-4 p-4"><Music size={48} aria-hidden="true" /><p className="text-sm break-all">{label}</p><audio src={fileUrl(photo.path)} aria-label={label} controls preload="metadata" className="w-full max-w-lg" onError={() => setFailed(true)} /></div>
    : <span className="flex h-full flex-col items-center justify-center gap-2 p-2"><Music size={32} aria-hidden="true" /><span className="text-xs line-clamp-2 break-all">{label}</span></span>;
  if (photo.kind === "video" || photo.content_type?.startsWith("video/")) return <video src={fileUrl(photo.path)} aria-label={label}
    controls={fullSize} preload="metadata" className="w-full h-full object-contain" onError={() => setFailed(true)} />;
  return <>
    {fullSize && !loaded && <span role="status" className="absolute inset-0 flex items-center justify-center text-stone-600">{t("Loading photo…")}</span>}
    <img src={fileUrl(fullSize ? photo.path : photo.thumbnail_path || photo.path)} alt={label} loading={fullSize ? "eager" : "lazy"}
      onLoad={() => setLoaded(true)} onError={() => setFailed(true)}
      className={fullSize ? "relative w-full h-full object-contain" : "w-full h-full object-cover"} />
  </>;
}

export default function MemberPhotoAlbum({ photos = [], memberName, albumTitle, onRemovePhoto, cardPhotoId, mediaRevision, renderPhotoActions, headerActions, showAll = false, disabled = false }) {
  useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const active = selected === null ? null : photos[selected];
  const mixedMedia = photos.some(photo => photo.kind === "audio" || photo.kind === "video" || /^(audio|video)\//.test(photo.content_type || ""));
  const photoLabel = (photo, index) => photo.filename || t("Photo {0}", { 0: index + 1 });
  const move = (amount) => {
    const index = Math.max(0, Math.min(photos.length - 1, selected + amount));
    setSelected(index); setPage(Math.floor(index / 24));
  };
  const changeOpen = (value) => { setOpen(value); setSelected(null); setPage(0); };
  const requestRemoval = (photo) => { setRemoveError(""); setPendingRemoval(mediaRevision === undefined ? photo : { ...photo, removal_revision: mediaRevision }); };
  const remove = async () => {
    setRemoving(true); setRemoveError("");
    try {
      await onRemovePhoto(pendingRemoval, true);
      const remaining = photos.filter((photo) => photo.id !== pendingRemoval.id);
      if (selected !== null) {
        const activeIndex = remaining.findIndex((photo) => photo.id === active?.id);
        setSelected(remaining.length ? activeIndex >= 0 ? activeIndex : Math.min(selected, remaining.length - 1) : null);
      }
      setPage(Math.min(page, Math.max(0, Math.ceil(remaining.length / 24) - 1)));
      setPendingRemoval(null);
    } catch (error) { setRemoveError(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setRemoving(false); }
  };
  const removeButton = (photo, label, overlay = false) => onRemovePhoto && <Button type="button" variant="destructive" size="sm"
    aria-label={t("Remove {0}", { 0: label })} title={t("Remove photo")} disabled={disabled || removing}
    onClick={() => requestRemoval(photo)}
    className={overlay ? "absolute bottom-1 right-1 z-10 h-9 w-9 p-0 shadow-md" : "absolute bottom-3 right-3 z-10 shadow-md"}>
    <Trash2 size={16} />{!overlay && <span className="ml-2">{t("Remove photo")}</span>}
  </Button>;
  const grid = (items, offset = 0) => <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
    {items.map((photo, index) => <div key={photo.id || photo.path || index} className="relative aspect-square">
      <button type="button"
      aria-label={t("View {0}", { 0: photoLabel(photo, index + offset) })}
      onClick={() => { setSelected(index + offset); setPage(Math.floor((index + offset) / 24)); setOpen(true); }}
      className="w-full h-full rounded-lg overflow-hidden border border-[#E6DEC3] bg-stone-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-600">
      <AlbumPhoto photo={photo} label={photoLabel(photo, index + offset)} />
      </button>
      {removeButton(photo, photoLabel(photo, index + offset), true)}
      {renderPhotoActions && <div className="absolute top-1 left-1 right-1 z-10 flex flex-wrap gap-1">{renderPhotoActions(photo)}</div>}
    </div>)}
  </div>;
  return <section className="mt-4" aria-label={t("Photo album")}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <h3 className="text-[11px] font-mono uppercase tracking-wider text-amber-800/70">{albumTitle || t("Photos")} ({photos.length})</h3>
      <div className="flex flex-wrap gap-2">
      <Button type="button" size="sm" variant="outline" onClick={() => changeOpen(true)} data-testid="view-album-button">
        <Images size={16} className="mr-2" />{t("View Album")}
      </Button>
      {headerActions}
      </div>
    </div>
    {photos.length ? grid(showAll ? photos : photos.slice(0, 6)) : <p className="text-sm text-stone-500">{t("No photos in this album yet.")}</p>}
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="sm:max-w-5xl bg-[#FBF8F1]" onKeyDown={(event) => {
        if (!active || event.altKey || event.ctrlKey || event.metaKey || event.target.closest?.("audio, video")) return;
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault(); move(event.key === "ArrowLeft" ? -1 : 1);
        }
      }}>
        <DialogHeader className="pr-10">
          <DialogTitle>{albumTitle || t("{0}'s photo album", { 0: memberName })}</DialogTitle>
          <DialogDescription>{t(mixedMedia ? "{0} media files" : "{0} photos", { 0: photos.length })}</DialogDescription>
        </DialogHeader>
        {active ? <>
          <div className="flex justify-between items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setSelected(null)}><Grid2X2 size={16} className="mr-2" />{t("Back to album")}</Button>
            <p role="status" className="text-sm text-stone-600">{t(mixedMedia ? "File {0} of {1}" : "Photo {0} of {1}", { 0: selected + 1, 1: photos.length })}</p>
          </div>
          <div className="relative h-[55dvh] min-h-40 bg-stone-100 rounded-lg overflow-hidden">
            <AlbumPhoto key={active.id || active.path || selected} photo={active} label={photoLabel(active, selected)} fullSize />
            {removeButton(active, photoLabel(active, selected))}
            {renderPhotoActions && <div className="absolute top-2 left-2 z-10 flex flex-wrap gap-2">{renderPhotoActions(active)}</div>}
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button type="button" variant="outline" aria-label={t("Previous photo")} disabled={selected === 0} onClick={() => move(-1)}><ChevronLeft size={20} /></Button>
            <p className="text-sm text-stone-700 text-center break-all">{photoLabel(active, selected)}</p>
            <Button type="button" variant="outline" aria-label={t("Next photo")} disabled={selected === photos.length - 1} onClick={() => move(1)}><ChevronRight size={20} /></Button>
          </div>
        </> : photos.length ? <>
          {grid(photos.slice(page * 24, (page + 1) * 24), page * 24)}
          {photos.length > 24 && <div className="flex items-center justify-between gap-2">
            <Button variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>{t("Previous page")}</Button>
            <span role="status">{t("Page {0} of {1}", { 0: page + 1, 1: Math.ceil(photos.length / 24) })}</span>
            <Button variant="outline" disabled={(page + 1) * 24 >= photos.length} onClick={() => setPage(page + 1)}>{t("Next page")}</Button>
          </div>}
        </> : <p className="py-12 text-center text-stone-500">{t("No photos in this album yet.")}</p>}
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!pendingRemoval} onOpenChange={(value) => { if (!value && !removing) setPendingRemoval(null); }}>
      <AlertDialogContent className="bg-[#FBF8F1]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t(pendingRemoval?.kind === "audio" || pendingRemoval?.kind === "video" ? "Remove media" : "Remove photo")}</AlertDialogTitle>
          <AlertDialogDescription>{t(pendingRemoval?.kind === "audio" || pendingRemoval?.kind === "video" ? "Remove this media file permanently?" : "Remove this photo permanently?")}</AlertDialogDescription>
          {pendingRemoval?.id === cardPhotoId && <p className="text-sm text-red-800">{t("This photo is used on the tree card. Removing it will use the first remaining default photo, or the placeholder when none remain.")}</p>}
        </AlertDialogHeader>
        <p className="text-sm break-all">{pendingRemoval?.filename || t("Photo")}</p>
        {removeError && <p role="alert" className="text-sm text-red-800">{t(removeError)}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={removing}>{t("Cancel")}</AlertDialogCancel>
          <Button variant="destructive" disabled={disabled || removing} onClick={remove}>{removing ? t("Removing…") : t("Confirm deletion")}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>;
}
